// src/lib/platform.js
//
// Single source of truth for "are we running inside the native iOS shell".
// Deliberately zero imports: reads the global that the Capacitor bridge
// injects into the webview before any app code runs. On the web that global
// is undefined, every export below returns false, and this module costs the
// web bundle nothing beyond its own body.
//
// Never add a user-agent check or a hostname check here. Those are exactly
// the ad hoc tests this module exists to replace.

function bridge() {
  if (typeof window === 'undefined') return null;
  return window.Capacitor || null;
}

/** True only inside the Capacitor native shell. False in every browser, including the installed PWA. */
export function isNative() {
  try {
    const c = bridge();
    if (!c) return false;
    if (typeof c.isNativePlatform === 'function') return c.isNativePlatform() === true;
    // Older bridges expose a boolean instead of a function.
    return c.isNative === true;
  } catch {
    return false;
  }
}

/** 'ios' | 'android' | 'web'. Always 'web' when the bridge is absent. */
export function nativePlatform() {
  try {
    const c = bridge();
    if (!c) return 'web';
    if (typeof c.getPlatform === 'function') return c.getPlatform();
    return c.platform || 'web';
  } catch {
    return 'web';
  }
}

/** True only in the native iOS shell. */
export function isIOSNative() {
  return isNative() && nativePlatform() === 'ios';
}

/** True when a named Capacitor plugin is actually registered on the bridge. */
export function hasNativePlugin(name) {
  try {
    const c = bridge();
    return !!(c && c.Plugins && c.Plugins[name]);
  } catch {
    return false;
  }
}

/**
 * Load a Capacitor plugin only on native. Resolves to null on web, so callers
 * can write `const m = await loadNative(() => import('@capacitor/browser'));`
 * and the browser never requests the chunk.
 */
export async function loadNative(importer) {
  if (!isNative()) return null;
  try {
    return await importer();
  } catch {
    return null;
  }
}

/** The app's custom URL scheme deep-link prefix. Kept here so no other file hardcodes it. */
export const NATIVE_DEEP_LINK_SCHEME = 'com.timohelps.app';

/** Origin the native webview serves from. Mirrors capacitor.config.ts `hostname`. */
export const NATIVE_WEB_ORIGIN = 'https://app.timohelps.com';
